/**
 * Store lists that keep each location (UAT UI-008). PURE.
 *
 * A store list with latitude and longitude columns is a list of exact stores,
 * not of chains: each row stays a location. Two things read it:
 * - store-visit audiences count visits at those points (the visit data has
 *   each venue's coordinates);
 * - in-store, exit and directed-shop checks count only those stores, by the
 *   fielding index's own location ids, matched here to the nearest location of
 *   the row's chain.
 * A list without coordinates is still matched to whole chains.
 */
import { MAX_OOH_PLACEMENTS } from '../engine/request';

/** Most stores one list keeps: the out-of-home lists' limit, one bound JSON value. */
export const MAX_STORE_LOCATIONS = MAX_OOH_PLACEMENTS;
/** Distinct chains one list may name: each is a search of the fielding index. */
export const MAX_STORE_CHAINS = 60;
/** How far a row's point may be from the fielding index's point for the same store. */
export const STORE_MATCH_METERS = 250;
/** The circle a store-visit count draws around each store: the venue's point sits inside its lot. */
export const STORE_VISIT_RADIUS_METERS = 150;

export interface StoreLocation {
    /** The row's store name as written ("Target #1234"). */
    name: string;
    /** The chain it belongs to ("Target"). */
    chain: string;
    lat: number;
    lng: number;
}

const LAT = ['lat', 'latitude', 'y'];
const LNG = ['lng', 'lon', 'long', 'longitude', 'x'];
const CHAIN = ['chain', 'chain name', 'retailer', 'banner', 'brand', 'account'];
const NAME = [
    'store',
    'store name',
    'name',
    'location',
    'location name',
    'site',
    'site name',
    'venue',
];

const norm = (h: string) => h.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
const col = (headers: string[], names: string[]) =>
    headers.findIndex((h) => names.includes(norm(h)));

function headerRow(rows: string[][]): number {
    for (let i = 0; i < Math.min(rows.length, 10); i++)
        if (col(rows[i], LAT) >= 0 && col(rows[i], LNG) >= 0) return i;
    return -1;
}

/** True when the rows are a table with latitude and longitude columns. */
export const hasCoordinates = (rows: string[][]) => headerRow(rows) >= 0;

/** "Target #1234", "Target Store 1234 - Dallas" and "TARGET 1234" all read "Target". */
export function chainNameOf(name: string): string {
    const cut = name
        .replace(/\s*[-–|(].*$/, '')
        .replace(/\s*(#|no\.?|store|str|unit|loc(ation)?)\s*\d.*$/i, '')
        .replace(/\s+\d[\d\s-]*$/, '')
        .trim();
    const base = cut || name.trim();
    // Shouted names ("TARGET") read as the chain does.
    return base === base.toUpperCase() && /[A-Z]/.test(base)
        ? base.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
        : base;
}

export function parseStoreLocationRows(rows: string[][]): {
    locations: StoreLocation[];
    errors: string[];
} {
    const h = headerRow(rows);
    if (h < 0)
        return {
            locations: [],
            errors: [
                'The list needs a latitude column (lat or latitude) and a longitude column (lng, lon or longitude).',
            ],
        };
    const headers = rows[h];
    const latCol = col(headers, LAT);
    const lngCol = col(headers, LNG);
    const chainCol = col(headers, CHAIN);
    const nameCol = col(headers, NAME);
    if (chainCol < 0 && nameCol < 0)
        return {
            locations: [],
            errors: [
                'The list needs a store or chain name column, so each store can be matched to its chain.',
            ],
        };
    const locations: StoreLocation[] = [];
    const errors: string[] = [];
    rows.slice(h + 1).forEach((row, i) => {
        const rowNo = h + i + 2;
        if (row.every((c) => !String(c ?? '').trim())) return;
        const cell = (c: number) => (c >= 0 ? String(row[c] ?? '').trim() : '');
        const lat = Number(cell(latCol));
        const lng = Number(cell(lngCol));
        if (
            !cell(latCol) ||
            !cell(lngCol) ||
            !Number.isFinite(lat) ||
            !Number.isFinite(lng) ||
            Math.abs(lat) > 90 ||
            Math.abs(lng) > 180
        ) {
            errors.push(`Row ${rowNo}: latitude and longitude must be numbers on the map.`);
            return;
        }
        const name = cell(nameCol) || cell(chainCol);
        const chain = cell(chainCol) || chainNameOf(name);
        if (!chain) {
            errors.push(`Row ${rowNo}: no store or chain name.`);
            return;
        }
        locations.push({
            name: (name || chain).slice(0, 160),
            chain: chain.slice(0, 160),
            lat,
            lng,
        });
    });
    if (locations.length > MAX_STORE_LOCATIONS)
        return {
            locations: [],
            errors: [
                `${locations.length.toLocaleString('en-US')} stores is more than one list takes (${MAX_STORE_LOCATIONS.toLocaleString('en-US')}). Split the list by market.`,
            ],
        };
    return { locations, errors };
}

/** Great-circle distance in meters. */
export function meters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const rad = (d: number) => (d * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
}

/** The fielding index's location nearest a row, within `maxMeters`; null when none is that close. */
export function nearestLocation(
    point: { lat: number; lng: number },
    candidates: { id: string; lat: number; lng: number }[],
    maxMeters = STORE_MATCH_METERS,
): string | null {
    let best: { id: string; d: number } | null = null;
    for (const c of candidates) {
        const d = meters(point, c);
        if (d <= maxMeters && (!best || d < best.d)) best = { id: c.id, d };
    }
    return best?.id ?? null;
}

/** Distinct chains in the order they first appear. */
export const chainsOf = (locations: StoreLocation[]) => [
    ...new Map(locations.map((l) => [l.chain.toLowerCase(), l.chain])).values(),
];
